import React, { Fragment, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import AntAPI from '../../utils/AntAPI';
import AntLocalization from '../../utils/AntLocalization';
import AntConfig from '../../utils/AntConfig';
import { AntGlobal } from '../../utils/AntContext';
import HeaderForm from './HeaderForm';
import Stage3ItemsImport from './Stage3ItemsImport';

// Stage 3 (review) container — bỏ stepper global, hiển thị 2 TABS:
//   • Tab "Thông tin chung"      → review form header fields (HeaderForm — AntBaseForm 1 cột)
//   • Tab "Danh sách sản phẩm"   → Stage3ItemsImport (sub-step mapping → preview)
// Footer button "Tạo phiếu" gọi `actionApply` API chuẩn AIVoucherAnalysis
// (KHÔNG dùng actionImport — vì luồng AI tạo voucher hoàn chỉnh, không phải
// bulk row insert).
//
// Tab labels có thể custom theo voucher type qua prop `tabLabels`.

function loadTableConfig(appGlobal, tableName) {
    if (!appGlobal || !tableName) return null;
    if (!(appGlobal.tableConfigs instanceof Array)) appGlobal.tableConfigs = [];
    var found = appGlobal.tableConfigs.find(function (t) { return t.tableName === tableName; });
    if (found) return found.config;
    var cnf = new AntConfig(appGlobal, tableName);
    cnf.configFields();
    appGlobal.tableConfigs.push({ tableName: tableName, config: cnf });
    return cnf;
}

function parseJSONField(value, fallback) {
    if (value == null || value === '') return fallback;
    if (typeof value === 'object') return value;
    try { return JSON.parse(value); } catch (e) { return fallback; }
}

var aivaAPI = null;
function getAPI() {
    if (!aivaAPI) aivaAPI = new AntAPI('ai', 'AIVoucherAnalysis');
    return aivaAPI;
}

function flattenHeader(h) {
    var out = {};
    if (!h) return out;
    Object.keys(h).forEach(function (k) {
        var info = h[k] || {};
        // Mọi field cho phép user-edit info.raw (tab "Thông tin chung" render input
        // cho mọi field). Ưu tiên info.raw — BE actionValidate sẽ re-coerce kiểu
        // (locale-aware parse cho number, parse FK cho text). Fallback info.resolved
        // khi raw rỗng (AI không bóc được).
        var v;
        if (info.raw != null && info.raw !== '') v = info.raw;
        else v = info.resolved;
        if (v != null && v !== '') out[k] = v;
    });
    return out;
}

function flattenItems(its, opts) {
    opts = opts || {};
    return (its || [])
        .filter(function (r) {
            if (opts.skipDropped && r && r.skip_flag) return false;
            return true;
        })
        .map(function (r) { return Object.assign({}, r.fields || {}); });
}

export default function Stage3TabbedReview(props) {
    var initial = props.analysis || {};
    var appGlobal = AntGlobal();
    var targetTable = initial.target_table;
    var analysisNotes = parseJSONField(initial.warnings_json, []).filter(function (finding) {
        return finding && !/^EXPITEM_/.test(finding.code || '');
    });
    var itemsTable = targetTable ? targetTable + 'Item' : null;
    var headerCnf = useMemo(function () { return loadTableConfig(appGlobal, targetTable); }, [targetTable]);
    var itemsCnf = useMemo(function () { return loadTableConfig(appGlobal, itemsTable); }, [itemsTable]);

    var [header, setHeader] = useState(parseJSONField(initial.proposed_header_json, {}));
    var [items, setItems] = useState(parseJSONField(initial.proposed_items_json, []));
    var [activeTab, setActiveTab] = useState('header'); // 'header' | 'items'
    var [applying, setApplying] = useState(false);
    var [revalidating, setRevalidating] = useState(false);
    var [error, setError] = useState(null);

    // ─── Debounced live revalidate on edit (giữ từ Stage3Review) ─────────
    var revalidateInFlight = useRef(false);
    var revalidateTimer = useRef(null);
    var pendingRef = useRef(false);
    var skipNextEffectRef = useRef(false);
    var firstRunRef = useRef(true);
    var draftRef = useRef();
    var revalidateRef = useRef();
    draftRef.current = JSON.stringify({ header: flattenHeader(header), items: flattenItems(items, { skipDropped: true }) });

    var revalidate = useCallback(function (opts) {
        opts = opts || {};
        if (revalidateInFlight.current) { pendingRef.current = true; return; }
        revalidateInFlight.current = true;
        setRevalidating(true);
        // BE `actionApply` đọc THẲNG `proposed_items_json` từ DB (đã lưu ở step
        // `actionValidate`) — args.edited_items chỉ là input cho re-validate +
        // re-save. → Để skip row có hiệu lực khi apply, PHẢI filter skip rows ra
        // ngay ở step revalidate. BE sẽ lưu proposed_items_json không chứa chúng,
        // apply tạo voucher với items đã filter.
        var requestedDraft = draftRef.current;
        var api = getAPI();
        api.post('/' + api.routeURL + '/actionValidate', {
            id: initial.id,
            items_import_mode: initial.items_import_mode || '',
            edited_header: flattenHeader(header),
            edited_items: flattenItems(items, { skipDropped: true })
        }, function (resp) {
            revalidateInFlight.current = false;
            setRevalidating(false);
            var d = (resp && resp.data) || resp || {};
            if (requestedDraft !== draftRef.current) {
                pendingRef.current = false;
                setTimeout(function () { revalidateRef.current({ silent: true }); }, 0);
                return;
            }
            if (d.error) { setError(d.message || 'Validate failed'); return; }
            skipNextEffectRef.current = true;
            setHeader(parseJSONField(d.proposed_header_json, header));
            // BE return list KHÔNG có skip rows (vì FE gửi filtered). FE setItems
            // = list mới → state không còn skip rows → grid render đúng số rows
            // visible (skip rows thực sự "bị xóa").
            setItems(parseJSONField(d.proposed_items_json, items));
            if (props.onChange) props.onChange(d);
            if (pendingRef.current) {
                pendingRef.current = false;
                setTimeout(function () { revalidateRef.current({ silent: true }); }, 0);
            }
        });
    }, [initial.id, initial.items_import_mode, header, items, props.onChange]);
    revalidateRef.current = revalidate;

    useEffect(function () {
        if (firstRunRef.current) { firstRunRef.current = false; return; }
        if (skipNextEffectRef.current) { skipNextEffectRef.current = false; return; }
        if (revalidateTimer.current) clearTimeout(revalidateTimer.current);
        revalidateTimer.current = setTimeout(function () { revalidate({ silent: true }); }, 700);
        return function () { if (revalidateTimer.current) clearTimeout(revalidateTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [header, items]);

    var apply = useCallback(function () {
        if (applying) return;
        setApplying(true);
        setError(null);
        var api = getAPI();
        api.post('/' + api.routeURL + '/actionApply', {
            id: initial.id,
            edited_header: flattenHeader(header),
            // Apply: bỏ skip rows ra khỏi payload — voucher tạo ra KHÔNG chứa các
            // dòng user đã đánh dấu skip ở grid preview.
            edited_items: flattenItems(items, { skipDropped: true })
        }, function (resp) {
            setApplying(false);
            var d = (resp && resp.data) || resp || {};
            if (d.error) { setError(d.message || 'Apply failed'); return; }
            if (props.onApplied) props.onApplied(d);
        });
    }, [initial.id, header, items, applying, props.onApplied]);

    var discard = useCallback(function () {
        var api = getAPI();
        api.post('/' + api.routeURL + '/actionDiscard', { id: initial.id }, function () {
            if (props.onClose) props.onClose();
        });
    }, [initial.id, props.onClose]);

    // Tab labels — custom được per voucher type
    var defaultLabels = {
        header: { vi: 'Thông tin chung', en: 'General Info', fr: 'Infos générales', es: 'Información general', id: 'Informasi umum', ja: '基本情報', zh: '基本信息', ko: '일반 정보', hi: 'सामान्य जानकारी', ar: 'معلومات عامة' },
        items: { vi: 'Danh sách sản phẩm', en: 'Items', fr: 'Articles', es: 'Artículos', id: 'Item', ja: '商品', zh: '商品', ko: '품목', hi: 'आइटम', ar: 'الأصناف' }
    };
    var tabLabels = Object.assign({}, defaultLabels, props.tabLabels || {});

    // Validation summary cho apply gating
    var okCount = items.filter(function (r) { return (!r.errors || !r.errors.length) && r.match_status === 'Resolved' && !r.skip_flag; }).length;
    var errCount = items.filter(function (r) {
        if (r.skip_flag) return false;
        return (r.errors && r.errors.length) || r.match_status !== 'Resolved';
    }).length;
    var hasValidRow = items.some(function (r) { return !r.skip_flag && r.match_status === 'Resolved'; });
    var canApply = !applying && !revalidating && errCount === 0 && hasValidRow;

    return (
        <Fragment>
            {error && <div className="ant-aiva-error">{error}</div>}
            {targetTable === 'EXPOrder' && analysisNotes.length > 0 && (
                <details className="ant-aiva-warning">
                    <summary>{AntLocalization.translateText({ vi: 'Lưu ý từ lần phân tích', en: 'Analysis findings', fr: 'Remarques de l’analyse', es: 'Notas del análisis', id: 'Catatan analisis', ja: '解析時の注意点', zh: '分析备注', ko: '분석 참고 사항', hi: 'विश्लेषण की टिप्पणियाँ', ar: 'ملاحظات التحليل' })} ({analysisNotes.length})</summary>
                    <ul>{analysisNotes.map(function (finding, index) {
                        return <li key={index}>{AntLocalization.translateText(finding.message || finding.code)}</li>;
                    })}</ul>
                </details>
            )}

            <div className="ant-aiva-tabs">
                <button
                    type="button"
                    className={'aiva-tab' + (activeTab === 'header' ? ' is-active' : '')}
                    onClick={function () { setActiveTab('header'); }}
                >
                    {AntLocalization.translateText(tabLabels.header)}
                </button>
                <button
                    type="button"
                    className={'aiva-tab' + (activeTab === 'items' ? ' is-active' : '')}
                    onClick={function () { setActiveTab('items'); }}
                >
                    {AntLocalization.translateText(tabLabels.items)}
                    {items.length > 0 && (
                        <span className="aiva-tab-badge">
                            {okCount}/{items.filter(function (r) { return !r.skip_flag; }).length}
                        </span>
                    )}
                </button>
                <div className="aiva-tabs-spacer" />
                {revalidating && (
                    <span className="aiva-tabs-status">
                        {AntLocalization.translateText({ vi: 'Đang kiểm tra…', en: 'Validating…', fr: 'Vérification…', es: 'Validando…', id: 'Memvalidasi…', ja: '検証中…', zh: '校验中…', ko: '검증 중…', hi: 'जाँच हो रही है…', ar: 'جارٍ التحقق…' })}
                    </span>
                )}
            </div>

            <div className="ant-aiva-tab-body">
                {activeTab === 'header' && (
                    <div className="aiva-tab-pane">
                        {typeof props.headerRenderer === 'function'
                            ? props.headerRenderer({
                                header: header,
                                onChange: setHeader,
                                config: headerCnf,
                                analysis: initial,
                                session: props.session
                            })
                            : <HeaderForm header={header} onChange={setHeader} table={targetTable} />}
                    </div>
                )}
                {activeTab === 'items' && (
                    <Stage3ItemsImport
                        items={items}
                        onChange={setItems}
                        config={itemsCnf}
                        headerConfig={headerCnf}
                        header={header}
                        analysis={initial}
                    />
                )}
            </div>

            <div className="pp-foot">
                <span className="footer-stats">
                    {canApply
                        ? (
                            <Fragment>
                                {AntLocalization.translateText({ vi: 'Sẵn sàng tạo phiếu', en: 'Ready to create voucher', fr: 'Prêt à créer le document', es: 'Listo para crear', id: 'Siap membuat dokumen', ja: '伝票を作成可能', zh: '可以创建单据', ko: '전표 생성 준비 완료', hi: 'वाउचर बनाने को तैयार', ar: 'جاهز لإنشاء المستند' })}{' '}
                                <b>{okCount} {AntLocalization.translateText({ vi: 'dòng', en: 'rows', fr: 'lignes', es: 'filas', id: 'baris', ja: '行', zh: '行', ko: '행', hi: 'पंक्ति', ar: 'صفوف' })}</b>
                            </Fragment>
                        )
                        : (errCount > 0
                            ? AntLocalization.translateText({ vi: 'Sửa hoặc bỏ qua các dòng chưa hợp lệ trước khi tạo phiếu.', en: 'Fix or skip invalid rows before creating voucher.', fr: 'Corrigez ou ignorez les lignes non valides avant de créer le document.', es: 'Corrige u omite las filas no válidas antes de crear el comprobante.', id: 'Perbaiki atau lewati baris tidak valid sebelum membuat dokumen.', ja: '伝票作成前に無効な行を修正またはスキップしてください。', zh: '创建单据前请修正或跳过无效行。', ko: '전표 생성 전에 유효하지 않은 행을 수정하거나 건너뛰세요.', hi: 'वाउचर बनाने से पहले अमान्य पंक्तियाँ सुधारें या छोड़ें।', ar: 'صحّح الصفوف غير الصالحة أو تخطَّها قبل إنشاء المستند.' })
                            : !hasValidRow
                                ? AntLocalization.translateText({ vi: 'Cần ít nhất một dòng hợp lệ.', en: 'At least one valid row required.', fr: 'Ajoutez au moins une ligne valide.', es: 'Agrega al menos una fila válida.', zh: '请至少添加一行有效内容。', ko: '유효한 행을 하나 이상 추가하세요.', hi: 'कम से कम एक मान्य पंक्ति जोड़ें।', ar: 'أضف صفًا صالحًا واحدًا على الأقل.', id: 'Perlu minimal satu baris valid.', ja: '有効な行が 1 行以上必要です。' })
                                : '')}
                </span>
                <button type="button" className="btn btn-danger" onClick={discard}>
                    {AntLocalization.translateText({ vi: 'Bỏ', en: 'Discard', fr: 'Abandonner', es: 'Descartar', id: 'Buang', ja: '破棄', zh: '放弃', ko: '버리기', hi: 'छोड़ें', ar: 'تجاهل' })}
                </button>
                <button type="button" className="btn btn-primary" disabled={!canApply} onClick={apply}>
                    {applying
                        ? AntLocalization.translateText({ vi: 'Đang tạo…', en: 'Creating…', fr: 'Création…', es: 'Creando…', id: 'Membuat…', ja: '作成中…', zh: '正在创建…', ko: '생성 중…', hi: 'बन रहा है…', ar: 'إنشاء…' })
                        : AntLocalization.translateText({ vi: 'Tạo phiếu', en: 'Create voucher', fr: 'Créer pièce', es: 'Crear documento', id: 'Buat voucher', ja: '伝票作成', zh: '创建单据', ko: '전표 생성', hi: 'वाउचर बनाएँ', ar: 'إنشاء سند' })}
                </button>
            </div>
        </Fragment>
    );
}
